/**
 * Survey Results Page
 *
 * Displays survey responses in a data grid with Excel export functionality.
 * Allows admins to view, sort, and export all survey responses.
 *
 * Features:
 * - Data grid with survey responses
 * - Sortable columns
 * - Excel/CSV export
 * - Response count and stats
 * - Links back to survey builder
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAuth, useIsSuperAdmin } from '@/hooks/useAuth';
import { surveysApi, ApiClientError } from '@/services/api';
import { DataGrid, type DataGridColumn } from '@/components/admin/DataGrid';
import type { SurveyForm, SurveyResponse, PaginationMeta } from '@/types';

/**
 * Loading spinner component
 */
function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'sm' ? 'h-4 w-4' : 'h-8 w-8';

  return (
    <svg className={`animate-spin ${sizeClasses} text-primary`} viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format cell value for display
 */
function formatResponseValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
}

/**
 * Convert survey responses to CSV format
 */
function convertToCSV(
  survey: SurveyForm,
  responses: SurveyResponse[]
): string {
  // Build header row
  const headers = ['Respondent ID', 'Submitted At'];
  survey.formSchema.questions.forEach((q) => {
    headers.push(q.label);
  });

  // Build data rows
  const rows = responses.map((response) => {
    const row: string[] = [
      response.respondentId,
      formatDate(response.submittedAt),
    ];

    survey.formSchema.questions.forEach((q) => {
      const value = response.responses[q.id];
      row.push(formatResponseValue(value));
    });

    return row;
  });

  // Escape CSV values
  const escapeCSV = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  // Combine headers and rows
  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map((row) => row.map(escapeCSV).join(',')),
  ].join('\n');

  return csvContent;
}

/**
 * Download data as file
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Survey Results Page Component
 */
export function SurveyResultsPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const isSuperAdmin = useIsSuperAdmin();

  const surveyId = params.id;

  // State
  const [survey, setSurvey] = useState<SurveyForm | null>(null);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const RESPONSES_PER_PAGE = 50;

  /**
   * Fetch survey and responses
   */
  const fetchData = useCallback(async () => {
    if (!surveyId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch survey and responses in parallel
      const [surveyData, responsesData] = await Promise.all([
        surveysApi.get(surveyId),
        surveysApi.getResponses(surveyId, {
          page: currentPage,
          limit: RESPONSES_PER_PAGE,
        }),
      ]);

      setSurvey(surveyData);
      setResponses(responsesData.data);
      setPagination(responsesData.pagination);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError
        ? err.message
        : 'Failed to load survey results';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [surveyId, currentPage]);

  /**
   * Initial load
   */
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchData();
    }
  }, [authLoading, isAuthenticated, fetchData]);

  /**
   * Redirect non-admin users
   */
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isSuperAdmin)) {
      navigate('/login', { state: { from: `/admin/surveys/${surveyId}/results` }, replace: true });
    }
  }, [authLoading, isAuthenticated, isSuperAdmin, navigate, surveyId]);

  /**
   * Build columns from survey schema
   */
  const columns = useMemo<DataGridColumn<SurveyResponse>[]>(() => {
    if (!survey) return [];

    const cols: DataGridColumn<SurveyResponse>[] = [
      {
        key: 'respondentId',
        label: 'Respondent',
        accessor: (row) => row.respondentId,
        sortable: true,
        minWidth: '150px',
        render: (value) => (
          <span className="font-mono text-xs truncate max-w-[150px] block" title={String(value)}>
            {String(value).substring(0, 8)}...
          </span>
        ),
      },
      {
        key: 'submittedAt',
        label: 'Submitted',
        accessor: (row) => row.submittedAt,
        sortable: true,
        minWidth: '160px',
        render: (value) => formatDate(String(value)),
      },
    ];

    // Add columns for each question
    survey.formSchema.questions.forEach((question) => {
      cols.push({
        key: question.id,
        label: question.label,
        accessor: (row) => row.responses[question.id],
        sortable: true,
        minWidth: '150px',
        render: (value) => {
          const formatted = formatResponseValue(value);
          return (
            <span
              className="block truncate max-w-[200px]"
              title={formatted}
            >
              {formatted}
            </span>
          );
        },
      });
    });

    return cols;
  }, [survey]);

  /**
   * Handle CSV export
   */
  const handleExportCSV = useCallback(async () => {
    if (!survey || !surveyId) return;

    try {
      setIsExporting(true);
      setError(null);

      // Fetch all responses for export (up to a reasonable limit)
      const allResponses = await surveysApi.getResponses(surveyId, {
        page: 1,
        limit: 10000, // Get all responses for export
      });

      const csv = convertToCSV(survey, allResponses.data);
      const filename = `${survey.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_responses_${new Date().toISOString().split('T')[0]}.csv`;

      downloadFile(csv, filename, 'text/csv;charset=utf-8;');

      setSuccessMessage(`Exported ${allResponses.data.length} responses successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError
        ? err.message
        : 'Failed to export responses';
      setError(errorMessage);
    } finally {
      setIsExporting(false);
    }
  }, [survey, surveyId]);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Don't render anything if not authorized (will redirect)
  if (!isAuthenticated || !isSuperAdmin) {
    return null;
  }

  // Show not found if no survey ID
  if (!surveyId) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="text-center">
          <h1 className="text-3xl font-display font-bold text-white mb-4">Survey Not Found</h1>
          <p className="text-white/60 mb-6">The requested survey could not be found.</p>
          <Link to="/admin" className="btn-primary">
            Back to Admin
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">
            <span className="text-gradient">Survey Results</span>
          </h1>
          {survey && (
            <p className="text-white/60 mt-1">{survey.title}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/admin/surveys/${surveyId}`}
            className="btn-secondary flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit Survey
          </Link>
          <Link to="/admin" className="btn-secondary">
            Back to Admin
          </Link>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
          <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-red-400">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-red-400/60 hover:text-red-400"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Success Banner */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3">
          <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-green-400">{successMessage}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : survey ? (
        <>
          {/* Stats Bar */}
          <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-primary"></div>
                  <span className="text-white/80">
                    <strong className="text-white">{pagination?.total ?? responses.length}</strong> total responses
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                  <span className="text-white/80">
                    <strong className="text-white">{survey.formSchema.questions.length}</strong> questions
                  </span>
                </div>
                {survey.isActive ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    <span className="text-green-400 text-sm">Active</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-white/40"></div>
                    <span className="text-white/40 text-sm">Inactive</span>
                  </div>
                )}
              </div>

              {/* Export Button */}
              <button
                type="button"
                onClick={handleExportCSV}
                disabled={isExporting || responses.length === 0}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Export to Excel
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Data Grid */}
          <div className="card p-0 overflow-hidden">
            <DataGrid
              columns={columns}
              data={responses}
              getRowKey={(row) => `${row.respondentId}-${row.submittedAt}`}
              emptyMessage="No responses yet. Share the survey to start collecting responses."
              maxHeight="600px"
            />
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <span className="text-white/60">
                Page {pagination.page} of {pagination.totalPages}
              </span>

              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNext}
                className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* Survey Link */}
          <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10">
            <h3 className="text-sm font-medium text-white/80 mb-2">Share this survey</h3>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}/member/surveys/${surveyId}`}
                className="input flex-1 text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/member/surveys/${surveyId}`);
                  setSuccessMessage('Link copied to clipboard!');
                  setTimeout(() => setSuccessMessage(null), 2000);
                }}
                className="btn-secondary px-4"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default SurveyResultsPage;
